import * as d3 from 'd3';
import { createLegend, createTooltip, displayDateFormat, formatValue, getGroupColor, GroupedValueNameData, hideTooltip, positionTooltipAt, roundDigitsAfterDecimal, setupTooltipHiding, shouldShowLegend, trimLabel } from '../cross_platform/dashboard_script';
import { SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';

interface ElementWithCleanup extends Element {
	_cleanupTooltip?: () => void;
}

export function createLineSvg({
	chartData,
	granularity,
	width,
	height,
	marginTop,
	marginBottom,
	marginLeft,
	marginRight,
	opts,
	tooltipId
}: {
	chartData: GroupedValueNameData[],
	granularity?: 'day' | 'week' | 'month' | 'year',
	width: number,
	height: number,
	marginTop: number,
	marginBottom: number,
	marginLeft: number,
	marginRight: number,
	opts?: { 
		locale?: string,
		number_extension?: string | number,
		colorGradient?: boolean,
		digits_after_decimal?: number,
		legend?: boolean,
		legendItemSpacing?: number,
		max_legend_items?: number,
		legend_orientation?: 'horizontal' | 'vertical',
		trim_x_labels?: number,
		trim_y_labels?: number,
		x_axis_legend?: string,
		y_axis_legend?: string,
		x_axis_boundaries?: {min?: number, max?: number},
		y_axis_boundaries?: {min?: number, max?: number}
	},
	tooltipId?: string
}): SVGSVGElement | null {
	if (!chartData || chartData.length === 0) return null;

	// parse date strings like "2025-12-01" into Date objects
	const parseDate = d3.utcParse('%Y-%m-%d');
	const parsed = chartData
		.map(d => ({ ...d, date: parseDate(String(d.name)) }))
		.filter(d => d.date instanceof Date && !isNaN(+d.date)) as Array<GroupedValueNameData & { date: Date }>;

	if (parsed.length === 0) return null;

	const gran = granularity ?? 'month';
	const locale = opts?.locale ?? 'fr-FR';

	// Data is already aggregated by caller — just map and sort by date
	const data = parsed
		.map(d => ({ date: d.date, value: d.value, name: displayDateFormat(d.date, gran, locale), group: d.group }))
		.sort((a, b) => +a.date - +b.date);

	const x = d3.scaleUtc()
		.domain(d3.extent(data, d => d.date) as [Date, Date])
		.range([marginLeft, width - marginRight]);

	// y scale (linear) with numeric domain
	const dataMax = d3.max(data, d => d.value) ?? 0;
	const yMin = opts?.y_axis_boundaries?.min !== undefined ? opts.y_axis_boundaries.min : 0;
	const yMax = opts?.y_axis_boundaries?.max !== undefined ? opts.y_axis_boundaries.max : dataMax;
	const y = d3.scaleLinear()
		.domain([yMin, yMax])
		.nice()
		.range([height - marginBottom, marginTop]);

	// line generator uses the parsed/truncated date field
	const line = d3.line<{ value: number; date: Date }>()
		.defined(d => d.date != null)
		.x(d => x(d.date))
		.y(d => y(d.value));

	const svg = d3.create('svg')
		.attr('width', width)
		.attr('height', height)
		.attr('viewBox', [0, 0, width, height])
		.attr('style', 'max-width: 100%; height: auto; height: intrinsic;');

	// x axis: ticks & formats depend on granularity
	const xAxis: d3.Axis<Date> = d3.axisBottom(x) as unknown as d3.Axis<Date>;
	xAxis.tickSizeOuter(0);
	switch (gran) {
		case 'year':
			xAxis.ticks(d3.utcYear.every(1)).tickFormat(opts?.trim_x_labels ? (d: Date) => trimLabel(displayDateFormat(d, 'year', locale), opts.trim_x_labels) : (d: Date) => displayDateFormat(d, 'year', locale));
			break;
		case 'month':
			xAxis.ticks(d3.utcMonth.every(1)).tickFormat(opts?.trim_x_labels ? (d: Date) => trimLabel(displayDateFormat(d, 'month', locale), opts.trim_x_labels) : (d: Date) => displayDateFormat(d, 'month', locale));
			break;
		case 'week':
			xAxis.ticks(d3.utcMonday.every(1)).tickFormat(opts?.trim_x_labels ? (d: Date) => trimLabel(displayDateFormat(d, 'week', locale), opts.trim_x_labels) : (d: Date) => displayDateFormat(d, 'week', locale));
			break;
		default:
			xAxis.ticks(Math.max(3, Math.floor(width / 80))).tickFormat(opts?.trim_x_labels ? (d: Date) => trimLabel(displayDateFormat(d, 'day', locale), opts.trim_x_labels) : (d: Date) => displayDateFormat(d, 'day', locale));
	}

	svg.append('g')
		.attr('transform', `translate(0,${height - marginBottom})`)
		.call(xAxis)
		.call(g => g.selectAll('text').each(function(this: SVGTextElement, d) {
			const date = d as Date;
			let fullText: string;
			switch (gran) {
				case 'year': fullText = displayDateFormat(date, 'year', locale); break;
				case 'month': fullText = displayDateFormat(date, 'month', locale); break;
				case 'week': fullText = displayDateFormat(date, 'week', locale); break;
				default: fullText = displayDateFormat(date, 'day', locale); break;
			}
			d3.select(this).append('title').text(fullText);
		}));

	// y axis with gridlines and label
	const yAxis = d3.axisLeft(y).ticks(height / 40);
	if (opts?.trim_y_labels) {
		yAxis.tickFormat((d: d3.NumberValue, _i: number) => {
			const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
			return trimLabel(String(value), opts.trim_y_labels);
		});
	} else {
		yAxis.tickFormat((d: d3.NumberValue, _i: number) => {
			const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
			return String(value);
		});
	}
	svg.append('g')
		.attr('transform', `translate(${marginLeft},0)`)
		.call(yAxis)
		.call(g => g.select('.domain').remove())
		.call(g => g.selectAll('.tick line').clone()
			.attr('x2', width - marginLeft - marginRight)
			.attr('stroke-opacity', 0.1))
		.call(g => g.selectAll('text').each(function(this: SVGTextElement, d) {
			const value = roundDigitsAfterDecimal(d as number, opts?.digits_after_decimal);
			d3.select(this).append('title').text(String(value));
		}));

	// Add x-axis legend (horizontal) if provided
	if (opts?.x_axis_legend) {
		svg.append('text')
			.attr('class', 'x-axis-legend')
			.attr('text-anchor', 'middle')
			.attr('x', marginLeft + (width - marginLeft - marginRight) / 2)
			.attr('y', height - 10)
			.attr('font-size', '12px')
			.attr('font-weight', '500')
			.attr('fill', 'var(--on-surface-color)')
			.text(opts.x_axis_legend);
	}

	// Add y-axis legend (vertical) if provided
	if (opts?.y_axis_legend) {
		svg.append('text')
			.attr('class', 'y-axis-legend')
			.attr('text-anchor', 'middle')
			.attr('transform', `translate(15, ${marginTop + (height - marginTop - marginBottom) / 2}) rotate(-90)`)
			.attr('font-size', '12px')
			.attr('font-weight', '500')
			.attr('fill', 'var(--on-surface-color)')
			.text(opts.y_axis_legend);
	}

	// Draw one line per group, each with a distinct color.
	const grouped = Array.from(d3.group(data, d => (d.group ?? ''))) as Array<[string, typeof data]>;

	const useColorGradient = Boolean(opts?.colorGradient);
	// defs for per-series gradients (create only if gradients are enabled)
	let defs: d3.Selection<SVGDefsElement, unknown, null, undefined> | null = null;
	if (useColorGradient) defs = svg.append('defs');

	// Compute total per group so we can sort and produce a consistent domain ordering
	const sums = grouped.map(([k, vals]) => ({ key: k, total: d3.sum(vals, v => v.value) }));
	const sortedKeys = sums.sort((a, b) => b.total - a.total).map(s => s.key);

	// Helper: pick readable text color (black or white) based on series color luminance
	const contrastTextFor = (col: string) => {
		const c = d3.color(col);
		if (!c) return 'white';
		const rgb = c.rgb();
		const lum = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
		return lum > 0.8 ? SidelyBlack : 'white';
	};
	// Create reusable tooltip
	const tooltipDiv = createTooltip({ tooltipId });

	let selectedGroup: string | null = null;

	// helper to position tooltip with bounds checking (reused logic from Pie)
	// If `focusScreenX` is provided, prefer placing the tooltip on the opposite side
	// of the focus marker so the marker isn't obscured by the tooltip.
	const positionTooltipWithFocus = (event: MouseEvent, focusScreenX?: number) => {
		positionTooltipAt(tooltipDiv, (event as MouseEvent & { pageX: number }).pageX, (event as MouseEvent & { pageY: number }).pageY, { focusScreenX });
	};

	// Create a focus marker (circle + label) that will follow the nearest point on hover/click
	// The label has a rounded-rect background (`.d3-focus-bg`) which we size to the text bbox
	const focus = svg.append('g').attr('class', 'd3-focus').style('display', 'none');
	focus.append('circle')
		.attr('r', 4)
		.attr('stroke', SurfaceColor)
		.attr('stroke-width', 1.5)
		.attr('fill', SidelyBlack);
	focus.append('rect').attr('class', 'd3-focus-bg').attr('rx', 2).attr('ry', 2).attr('fill', SidelyBlack).style('pointer-events', 'none');
	focus.append('text')
		.attr('x', 0)
		.attr('text-anchor', 'middle')
		.attr('y', '-10')
		.attr('font-size', 11)
		.attr('font-weight', '600')
		.style('pointer-events', 'none');

	// Legend: render only when requested in opts (property `legend`).
	// If any series has an undefined/empty group key (we normalized undefined to ''),
	// do not show a legend because there's nothing meaningful to label.
	const showLegend = shouldShowLegend(opts, sortedKeys);
	if (showLegend) {
		createLegend(svg, {
			sortedKeys,
			width,
			marginLeft,
			marginRight,
			leftPosition: 0,
			marginTop,
			itemSpacing: opts?.legendItemSpacing,
			maxItems: opts?.max_legend_items,
			legendOrientation: opts?.legend_orientation
		});
	}

	// Draw series and attach interactions
	grouped.forEach(([groupKey, values], gi) => {
		const series = values.slice().sort((a, b) => +a.date - +b.date);


		if (useColorGradient && defs) {
			// create a vertical gradient that fades the series color to transparent
			const color = String(getGroupColor(groupKey));
			const gradId = `line-gradient-${tooltipId ?? 'global'}-${gi}`;
			const lg = defs.append('linearGradient')
				.attr('id', gradId)
				.attr('x1', '0')
				.attr('y1', '0')
				.attr('x2', '0')
				.attr('y2', '1')
				.attr('gradientUnits', 'objectBoundingBox');
			lg.append('stop').attr('offset', '0%').attr('stop-color', color).attr('stop-opacity', '0.35');
			lg.append('stop').attr('offset', '100%').attr('stop-color', color).attr('stop-opacity', '0');

			// area under the line, filled with the gradient
			const area = d3.area<{ value: number; date: Date }>()
				.defined(d => d.date != null)
				.x(d => x(d.date))
				.y0(height - marginBottom)
				.y1(d => y(d.value));
			svg.append('path')
				.datum(series)
				.attr('class', 'd3-area')
				.attr('fill', `url(#${gradId})`)
				.attr('d', area(series) as string)
				.style('pointer-events', 'none');
		}

		const pathSel = svg.append('path')
			.classed('d3-line', true)
			.attr('fill', 'none')
			.attr('stroke', getGroupColor(groupKey))
			.attr('stroke-width', '1.5')
			.attr('stroke-linejoin', 'round')
			.attr('stroke-linecap', 'round')
			.attr('d', line(series) as string)
			.style('pointer-events', 'none');

		// Create an invisible but thicker hit area path on top to make hover/click easier
		const hitWidth = 12; // px, adjust as needed
		const hitSel = svg.append('path')
			.attr('fill', 'none')
			.attr('stroke', 'transparent')
			.attr('stroke-width', String(hitWidth))
			.attr('stroke-linejoin', 'round')
			.attr('stroke-linecap', 'round')
			.attr('d', line(series) as string);

		// reference to the visible path for styling changes when hit path receives events
		const visibleNode = pathSel.node() as unknown as d3.BaseType;

		hitSel
			.on('mouseover', (event: MouseEvent) => {
				const [mx] = d3.pointer(event, svg.node());
				const xDate = x.invert(mx as number);
				const bisect = d3.bisector((d: { date: Date }) => d.date).left;
				let i = bisect(series, xDate);
				if (i > 0 && i < series.length) {
					const left = series[i - 1];
					const right = series[i];
					i = (Math.abs(+xDate - +left.date) <= Math.abs(+right.date - +xDate)) ? (i - 1) : i;
				}
				const point = series[Math.max(0, Math.min(series.length - 1, i))];

				d3.select(visibleNode).attr('stroke-width', '3');
				{
					const headerHtml = groupKey ? `<div style="font-weight:600">${groupKey}</div><div style="opacity:0.6">${point.name}</div>` : `<div style="font-weight:600">${point.name}</div>`;
					tooltipDiv.innerHTML = headerHtml;
				}
				{
					const svgNode = svg.node() as SVGSVGElement;
					const svgRect = svgNode.getBoundingClientRect();
					const vbWidth = (svgNode.viewBox && svgNode.viewBox.baseVal && svgNode.viewBox.baseVal.width) ? svgNode.viewBox.baseVal.width : width;
					const scaleX = svgRect.width / vbWidth;
					const focusScreenX = svgRect.left + x(point.date) * scaleX;
					positionTooltipWithFocus(event, focusScreenX);
				}
				focus.style('display', 'block');
				focus.attr('transform', `translate(${x(point.date)},${y(point.value)})`);
				focus.select('circle').attr('fill', getGroupColor(groupKey));
				focus.select('text').text(formatValue(roundDigitsAfterDecimal(point.value, opts?.digits_after_decimal), opts));
				// size and color the background rect to match the text
				{
					const textNode = (focus.select('text').node() as SVGTextElement | null);
					if (textNode) {
						const bbox = textNode.getBBox();
						const padX = 6;
						const padY = 2;
						focus.select('rect.d3-focus-bg')
							.attr('x', bbox.x - padX)
							.attr('y', bbox.y - padY)
							.attr('width', bbox.width + padX * 2)
							.attr('height', bbox.height + padY * 2)
							.attr('fill', getGroupColor(groupKey));
						// set readable text color based on background
						focus.select('text').attr('fill', contrastTextFor(String(getGroupColor(groupKey))));
					}
				}
			})
			.on('mousemove', (event: MouseEvent) => {
				const [mx] = d3.pointer(event, svg.node());
				const xDate = x.invert(mx as number);
				const bisect = d3.bisector((d: { date: Date }) => d.date).left;
				let i = bisect(series, xDate);
				if (i > 0 && i < series.length) {
					const left = series[i - 1];
					const right = series[i];
					i = (Math.abs(+xDate - +left.date) <= Math.abs(+right.date - +xDate)) ? (i - 1) : i;
				}
				const point = series[Math.max(0, Math.min(series.length - 1, i))];

				{
					const headerHtml = groupKey ? `<div style="font-weight:600">${groupKey}</div><div style="opacity:0.6">${point.name}</div>` : `<div style="font-weight:600">${point.name}</div>`;
					tooltipDiv.innerHTML = headerHtml;
				}
				{
					const svgNode = svg.node() as SVGSVGElement;
					const svgRect = svgNode.getBoundingClientRect();
					const vbWidth = (svgNode.viewBox && svgNode.viewBox.baseVal && svgNode.viewBox.baseVal.width) ? svgNode.viewBox.baseVal.width : width;
					const scaleX = svgRect.width / vbWidth;
					const focusScreenX = svgRect.left + x(point.date) * scaleX;
					positionTooltipWithFocus(event, focusScreenX);
				}
				focus.style('display', 'block');
				focus.attr('transform', `translate(${x(point.date)},${y(point.value)})`);
				focus.select('circle').attr('fill', getGroupColor(groupKey));
				focus.select('text').text(formatValue(roundDigitsAfterDecimal(point.value, opts?.digits_after_decimal), opts));
				// size and color the background rect to match the text
				{
					const textNode = (focus.select('text').node() as SVGTextElement | null);
					if (textNode) {
						const bbox = textNode.getBBox();
						const padX = 6;
						const padY = 2;
						focus.select('rect.d3-focus-bg')
							.attr('x', bbox.x - padX)
							.attr('y', bbox.y - padY)
							.attr('width', bbox.width + padX * 2)
							.attr('height', bbox.height + padY * 2)
							.attr('fill', getGroupColor(groupKey));
						// set readable text color based on background
						focus.select('text').attr('fill', contrastTextFor(String(getGroupColor(groupKey))));
					}
				}
			})
			.on('mouseout', (_event: MouseEvent) => {
				const isSelected = selectedGroup === groupKey;
				if (!isSelected) {
					d3.select(visibleNode).attr('stroke-width', '1.5');
					focus.style('display', 'none');
					hideTooltip(tooltipDiv);
				}
			})
			.on('click', (event: MouseEvent) => {
				const [mx] = d3.pointer(event, svg.node());
				const xDate = x.invert(mx as number);
				const bisect = d3.bisector((d: { date: Date }) => d.date).left;
				let i = bisect(series, xDate);
				if (i > 0 && i < series.length) {
					const left = series[i - 1];
					const right = series[i];
					i = (Math.abs(+xDate - +left.date) <= Math.abs(+right.date - +xDate)) ? (i - 1) : i;
				}
				const point = series[Math.max(0, Math.min(series.length - 1, i))];

				if (selectedGroup === groupKey) {
					// deselect
					selectedGroup = null;
					d3.select(visibleNode).attr('stroke-width', '1.5');
					hideTooltip(tooltipDiv);
					focus.style('display', 'none');
				} else {
					// deselect previous visible lines
					svg.selectAll<SVGPathElement, unknown>('path.d3-line').attr('stroke-width', '1.5');
					selectedGroup = groupKey;
					d3.select(visibleNode).attr('stroke-width', '3');
					{
						const headerHtml = groupKey ? `<div style="font-weight:600">${groupKey}</div><div style="opacity:0.6">${point.name}</div>` : `<div style="font-weight:600">${point.name}</div>`;
						tooltipDiv.innerHTML = headerHtml;
					}
					{
						const svgNode = svg.node() as SVGSVGElement;
						const svgRect = svgNode.getBoundingClientRect();
						const vbWidth = (svgNode.viewBox && svgNode.viewBox.baseVal && svgNode.viewBox.baseVal.width) ? svgNode.viewBox.baseVal.width : width;
						const scaleX = svgRect.width / vbWidth;
						const focusScreenX = svgRect.left + x(point.date) * scaleX;
						positionTooltipWithFocus(event, focusScreenX);
					}
					focus.style('display', 'block');
					focus.attr('transform', `translate(${x(point.date)},${y(point.value)})`);
					focus.select('circle').attr('fill', getGroupColor(groupKey));
					focus.select('text').text(formatValue(roundDigitsAfterDecimal(point.value, opts?.digits_after_decimal), opts));
					// size and color the background rect to match the text
					{
						const textNode = (focus.select('text').node() as SVGTextElement | null);
						if (textNode) {
							const bbox = textNode.getBBox();
							const padX = 6;
							const padY = 2;
							focus.select('rect.d3-focus-bg')
								.attr('x', bbox.x - padX)
								.attr('y', bbox.y - padY)
								.attr('width', bbox.width + padX * 2)
								.attr('height', bbox.height + padY * 2)
								.attr('fill', getGroupColor(groupKey));
							// set readable text color based on background
							focus.select('text').attr('fill', contrastTextFor(String(getGroupColor(groupKey))));
						}
					}
				}
			});
	});

	{
		const focusNode = (focus.node() as SVGGElement | null);
		if (focusNode && focusNode.parentNode) {
			focusNode.parentNode.appendChild(focusNode);
		}
	}

	// Setup global tooltip hiding with cleanup
	const cleanup = setupTooltipHiding(tooltipDiv, () => {
		focus.style('display', 'none');
		selectedGroup = null;
		// restore line widths
		svg.selectAll<SVGPathElement, unknown>('path.d3-line').attr('stroke-width', '1.5');
	});

	const svgNode = svg.node();
	if (!svgNode) return null;
	
	// Store cleanup function on the SVG element
	(svgNode as ElementWithCleanup)._cleanupTooltip = cleanup;
	
	return svgNode;
}